import { ApolloClient, HttpLink, InMemoryCache, split } from '@apollo/client'
import { setContext } from '@apollo/client/link/context'
import { GraphQLWsLink } from '@apollo/client/link/subscriptions'
import {
    getMainDefinition,
    offsetLimitPagination,
} from '@apollo/client/utilities'
import { createClient } from 'graphql-ws'
import { useMemo } from 'react'

import { APP_ENV } from 'src/env'
import { CYPRESS_OPTIONS, IS_CYPRESS } from 'src/utils/misc'

/**
 * @param {boolean} isTTXUser
 * @param {string} ttxInviteToken
 * @returns {ApolloClient|null}
 */
function createTTXUserApolloClient(isTTXUser, ttxInviteToken) {
    if (!isTTXUser) {
        return null
    }

    // For queries and mutations
    const httpLink = new HttpLink({
        uri: APP_ENV.REACT_APP_GRAPHQL_ENDPOINT,
        headers: {
            'content-type': 'application/json',
        },
    })

    // Attaches headers to httpLink
    const authMiddleware = setContext((_operation, prevContext) => {
        const returnValue = {
            ...prevContext,
            headers: createHeadersForTTXUser(ttxInviteToken),
        }
        return returnValue
    })

    // For subscriptions over Websockets
    const wsLink = new GraphQLWsLink(
        createClient({
            url: APP_ENV.REACT_APP_WS_GRAPHQL_ENDPOINT,
            shouldRetry: () => {
                return true
            },
            retryAttempts: 5,
            connectionParams: () => {
                return {
                    headers: createHeadersForTTXUser(ttxInviteToken),
                }
            },
        })
    )

    // Optionally use one of the above depending on
    // the kind of operation
    /**
     * @example if gql query operation is subscription then use wsLink else use the httpLink for queries and mutations
     */
    const splitLink = split(
        ({ query }) => {
            const definition = getMainDefinition(query)
            return (
                definition.kind === 'OperationDefinition' &&
                definition.operation === 'subscription'
            )
        },
        wsLink,
        httpLink
    )

    const client = new ApolloClient({
        connectToDevTools: true,
        link: authMiddleware.concat(splitLink),
        cache: new InMemoryCache({
            typePolicies: {
                Query: {
                    fields: {
                        alerts_alertGroup: offsetLimitPagination([
                            'where',
                            'order_by',
                        ]),
                        assets_asset: offsetLimitPagination([
                            'where',
                            'order_by',
                        ]),
                    },
                },
                users_user: {
                    fields: {
                        fullName: {
                            read(_existing, { readField }) {
                                return `${readField('firstName')}${
                                    ' ' + readField('lastName')
                                }`
                            },
                        },
                    },
                },
            },
        }),
    })
    return client
}

function createAuthUserApolloClient(config) {
    const { accessToken, organizationId, isSimulationEnabled, simulationId } =
        config
    // For queries and mutations
    const httpLink = new HttpLink({
        uri: APP_ENV.REACT_APP_GRAPHQL_ENDPOINT,
        headers: {
            'content-type': 'application/json',
        },
    })

    // Attaches headers to httpLink
    const authMiddleware = setContext((_operation, prevContext) => {
        const returnValue = {
            ...prevContext,
            headers: createHeadersForAuthUser(
                prevContext.headers,
                accessToken,
                organizationId,
                isSimulationEnabled,
                simulationId
            ),
        }
        return returnValue
    })

    // For subscriptions over Websockets
    const wsLink = new GraphQLWsLink(
        createClient({
            url: APP_ENV.REACT_APP_WS_GRAPHQL_ENDPOINT,
            shouldRetry: () => {
                return true
            },
            retryAttempts: 5,
            connectionParams: () => {
                return {
                    headers: createHeadersForAuthUser(
                        {},
                        accessToken,
                        organizationId,
                        isSimulationEnabled,
                        simulationId
                    ),
                }
            },
        })
    )

    // Optionally use one of the above depending on
    // the kind of operation
    /**
     * @example if gql query operation is subscription then use wsLink else use the httpLink for queries and mutations
     */
    const splitLink = split(
        ({ query }) => {
            const definition = getMainDefinition(query)
            return (
                definition.kind === 'OperationDefinition' &&
                definition.operation === 'subscription'
            )
        },
        wsLink,
        httpLink
    )

    const client = new ApolloClient({
        connectToDevTools: true,
        link: authMiddleware.concat(splitLink),
        cache: new InMemoryCache({
            typePolicies: {
                Query: {
                    fields: {
                        alerts_alertGroup: offsetLimitPagination([
                            'where',
                            'order_by',
                        ]),
                        assets_asset: offsetLimitPagination([
                            'where',
                            'order_by',
                        ]),
                    },
                },
                users_user: {
                    fields: {
                        fullName: {
                            read(_existing, { readField }) {
                                return `${readField('firstName')}${
                                    ' ' + readField('lastName')
                                }`
                            },
                        },
                    },
                },
            },
        }),
    })
    return client
}

/**
 * @param {string} token
 * @returns {Record<string,string>}
 */
function createHeadersForTTXUser(token) {
    return {
        'x-ttx-user': 'true',
        'x-ttx-token': token,
    }
}

/**
 * @param {any} previousHeaders
 * @param {string} accessToken
 * @param {string} organizationId
 * @param {boolean} isSimulationEnabled
 * @param {string} simulationId
 * @returns {Record<string,string>}
 */
function createHeadersForAuthUser(
    previousHeaders,
    accessToken,
    organizationId,
    isSimulationEnabled,
    simulationId
) {
    if (IS_CYPRESS) {
        return {
            ...previousHeaders,
            ...CYPRESS_OPTIONS.headers,
            'organization-id': CYPRESS_OPTIONS.userOrganizationId,
            'content-type': 'application/json',
        }
    }

    return {
        ...previousHeaders,
        Authorization: `Bearer ${accessToken}`,
        'organization-id': organizationId,
        'content-type': 'application/json',
        ...(isSimulationEnabled && {
            // -------
            // NOTE: Not using a boolean here because it crashes the WS Link
            // when simulation mode is enabled
            'x-simulation-enabled': 'true',
            // -------
            'x-simulation-id': simulationId,
        }),
    }
}
/**
 * @typedef {Object} Config
 * @property {string} accessToken Auth token from Auth0
 * @property {string} organizationId User's organizationId
 * @property {boolean} isTTXUser
 * @property {string} ttxInviteToken TTX Invite Token
 * @property {boolean} isSimulationEnabled simulation mode flag
 * @property {string} simulationId Current Simulation Id
 */

/**
 * @param {Config} config
 */
function useORNAApolloClient(config) {
    const {
        accessToken,
        organizationId,
        isTTXUser,
        ttxInviteToken,
        isSimulationEnabled = false,
        simulationId,
    } = config

    const authUserClient = useMemo(() => {
        const client = createAuthUserApolloClient({
            accessToken,
            organizationId,
            isSimulationEnabled,
            simulationId,
        })

        return client
    }, [accessToken, isSimulationEnabled, organizationId, simulationId])

    const ttxUserClient = useMemo(() => {
        return createTTXUserApolloClient(isTTXUser, ttxInviteToken)
    }, [isTTXUser, ttxInviteToken])

    return ttxUserClient || authUserClient
}

export default useORNAApolloClient
